import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useTenant } from "@/tenant/TenantProvider";
import { useOrder } from "@/store/orderStore";
import DishRow from "@/components/DishRow";
import OptionModal from "@/components/OptionModal";
import PhotoOverlay from "@/components/PhotoOverlay";
import StickyCart from "@/components/StickyCart";
import AiHint from "@/components/AiHint";
import { sumMacros } from "@/lib/format";
import { TAG_GROUPS } from "@/lib/drinkTags";
import { JUMP_OFFSET, useScrollSpy } from "@/hooks/useScrollSpy";

/**
 * The whole menu on one screen: every category, then drinks, one scroll.
 *
 * BuildPlate is the filtered browse — one category at a time. This screen's job
 * is "see everything", so the chips up top are a table of contents: they move
 * the viewport and never filter, and the list under them never changes shape.
 */
const Menu = ({ onBack, onViewCart, onAuto }) => {
  const { state, addToCart, decrementCart } = useOrder();
  const { categories, drinkSubcategories, dishes: allDishes, findDish } = useTenant();
  const { t } = useTranslation();
  const [optDish, setOptDish] = useState(null);
  const [photoDish, setPhotoDish] = useState(null);

  // Browsing never shows stop-listed dishes; findDish still resolves them.
  // A category the stop-list has emptied loses its section and its chip.
  const sections = useMemo(() => {
    const available = allDishes.filter((d) => d.available !== false);
    const food = categories
      .map((c) => ({
        id: c.id,
        emoji: c.emoji,
        short: c.short,
        name: c.name,
        dishes: available.filter((d) => d.category === c.id),
      }))
      .filter((s) => s.dishes.length > 0);

    // Drinks close the menu, grouped as the drinks screen groups them. A drink
    // whose tag matches no group still gets rows — never silently invisible.
    const drinks = available.filter((d) => d.category === "drinks");
    const grouped = new Set();
    const drinkGroups = drinkSubcategories
      .map((c) => {
        const tags = new Set(TAG_GROUPS[c.id] || []);
        const list = drinks.filter((d) => tags.has(d.tag));
        list.forEach((d) => grouped.add(d.id));
        return { id: c.id, emoji: c.emoji, name: c.name, dishes: list };
      })
      .filter((g) => g.dishes.length > 0);
    const stray = drinks.filter((d) => !grouped.has(d.id));
    if (stray.length > 0) drinkGroups.push({ id: "other", emoji: "", name: "", dishes: stray });

    return { food, drinkGroups, drinkCount: drinks.length };
  }, [allDishes, categories, drinkSubcategories]);

  const totals = useMemo(() => sumMacros(state.cart, findDish), [state.cart, findDish]);
  const cartCount = state.cart.reduce((s, it) => s + it.qty, 0);

  const qtyMap = useMemo(() => {
    const m = {};
    state.cart.forEach((it) => {
      m[it.dishId] = (m[it.dishId] || 0) + it.qty;
    });
    return m;
  }, [state.cart]);

  const handleAdd = (dish) => {
    if (dish.doneness || dish.style || dish.milk || dish.variant) {
      setOptDish(dish);
    } else {
      addToCart(dish.id);
    }
  };

  const handleDec = (dish) => {
    // remove the last matching cart line for this dish
    const lines = state.cart.filter((it) => it.dishId === dish.id);
    if (lines.length === 0) return;
    const last = lines[lines.length - 1];
    decrementCart(last.uid);
  };

  const sectionIds = useMemo(
    () => [
      ...sections.food.map((s) => s.id),
      ...(sections.drinkGroups.length > 0 ? ["drinks"] : []),
    ],
    [sections]
  );
  const { jumpTo, chipClass } = useScrollSpy(sectionIds, "menu-section-", "menu-jump-");

  return (
    <div className="pb-32" data-testid="menu-screen">
      <div className="sticky top-0 z-20 bg-[#F9F9F7]/95 backdrop-blur border-b border-neutral-100">
        <div className="px-5 pt-6 pb-3 flex items-center gap-3">
          <button
            data-testid="menu-back"
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
            aria-label={t("common.back")}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
              {t("common.menu")}
            </div>
            <div className="text-base font-bold text-neutral-900 -mt-0.5">{t("menu.title")}</div>
          </div>
        </div>
        <div className="overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-5 pb-3 min-w-max">
            {sections.food.map((s) => (
              <button
                key={s.id}
                id={`menu-jump-${s.id}`}
                data-testid={`menu-jump-${s.id}`}
                onClick={() => jumpTo(s.id)}
                className={chipClass(s.id)}
              >
                <span>{s.emoji}</span>
                <span>{s.short}</span>
              </button>
            ))}
            {sections.drinkGroups.length > 0 && (
              <button
                id="menu-jump-drinks"
                data-testid="menu-jump-drinks"
                onClick={() => jumpTo("drinks")}
                className={chipClass("drinks")}
              >
                <span>🥤</span>
                <span>{t("drinks.title")}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 pt-4">
        <AiHint onAuto={onAuto} testId="menu-ai-hint" />
      </div>

      {sections.food.map((s) => (
        <div
          key={s.id}
          id={`menu-section-${s.id}`}
          data-testid={`menu-section-${s.id}`}
          className={`px-5 pt-5 ${JUMP_OFFSET}`}
        >
          <h2 className="text-2xl font-black tracking-tight text-neutral-900 mb-1">
            {s.emoji} {s.name}
          </h2>
          {/* "K IDR" is the price unit itself and stays put in every language —
              see lib/format.js. */}
          <p className="text-xs text-neutral-500 mb-4 font-mono uppercase tracking-wider">
            {t("build.countAndPrices", { count: s.dishes.length })}
          </p>
          <div className="bg-white border border-neutral-100 rounded-2xl px-4 shadow-sm">
            {s.dishes.map((d) => (
              <DishRow
                key={d.id}
                dish={d}
                qtyInCart={qtyMap[d.id] || 0}
                onAdd={handleAdd}
                onDec={handleDec}
                onOpenPhoto={setPhotoDish}
              />
            ))}
          </div>
        </div>
      ))}

      {sections.drinkGroups.length > 0 && (
        <div
          id="menu-section-drinks"
          data-testid="menu-section-drinks"
          className={`px-5 pt-5 ${JUMP_OFFSET}`}
        >
          <h2 className="text-2xl font-black tracking-tight text-neutral-900 mb-1">
            {t("drinks.title")}
          </h2>
          <p className="text-xs text-neutral-500 mb-4 font-mono uppercase tracking-wider">
            {t("build.countAndPrices", { count: sections.drinkCount })}
          </p>
          {sections.drinkGroups.map((g) => (
            <div key={g.id} className="mb-4">
              {g.name && (
                <div className="text-sm font-bold text-neutral-700 mb-2">
                  {g.emoji} {g.name}
                </div>
              )}
              <div className="bg-white border border-neutral-100 rounded-2xl px-4 shadow-sm">
                {g.dishes.map((d) => (
                  <DishRow
                    key={d.id}
                    dish={d}
                    qtyInCart={qtyMap[d.id] || 0}
                    onAdd={handleAdd}
                    onDec={handleDec}
                    onOpenPhoto={setPhotoDish}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <StickyCart cartCount={cartCount} totals={totals} onView={onViewCart} />

      {optDish && (
        <OptionModal
          dish={optDish}
          onClose={() => setOptDish(null)}
          onConfirm={(opts) => {
            addToCart(optDish.id, opts);
            setOptDish(null);
          }}
        />
      )}
      {photoDish && <PhotoOverlay dish={photoDish} onClose={() => setPhotoDish(null)} />}
    </div>
  );
};

export default Menu;
